import { useId, useState } from "react";
import { HiChevronDown } from "react-icons/hi2";

import { isStandalone } from "../pwa/install";
import { currentPlatform, type DevicePlatform } from "../web-push";

interface GuideSection {
  title: string;
  steps: string[];
}

interface Guide {
  label: string;
  sections: GuideSection[];
}

/** Шаги в браузере на компьютере одинаковы, различается только система вокруг. */
const desktopBrowser: GuideSection = {
  title: "В браузере",
  steps: [
    "Нажмите на значок слева от адреса сайта и убедитесь, что для пункта «Уведомления» выбрано «Разрешить».",
    "Если разрешение отклонено, смените его на «Разрешить», обновите страницу и снова нажмите «Подключить это устройство».",
  ],
};

const guides: Record<DevicePlatform, Guide> = {
  windows: {
    label: "Windows",
    sections: [
      {
        title: "В Windows",
        steps: [
          "Откройте «Пуск» → «Параметры» → «Система» → «Уведомления» (в Windows 10 — «Уведомления и действия»).",
          "Включите уведомления: в Windows 11 это переключатель «Уведомления», в Windows 10 — «Получать уведомления от приложений и других отправителей».",
          "В списке отправителей ниже найдите свой браузер — Chrome, Edge, Firefox или Яндекс Браузер — и включите его.",
          "Выключите «Не беспокоить» (Windows 11) или «Помощник по фокусировке» (Windows 10): в этих режимах уведомления не всплывают, а только копятся в центре уведомлений.",
        ],
      },
      desktopBrowser,
    ],
  },
  macos: {
    label: "macOS",
    sections: [
      {
        title: "В macOS",
        steps: [
          "Откройте меню Apple → «Системные настройки» → «Уведомления».",
          "Найдите свой браузер в списке и включите «Допускать уведомления».",
          "Отключите «Не беспокоить» или «Фокусирование» в Пункте управления: в этих режимах уведомления не появляются.",
        ],
      },
      desktopBrowser,
    ],
  },
  linux: {
    label: "Linux",
    sections: [
      {
        title: "В системе",
        steps: [
          "Убедитесь, что в окружении работает служба уведомлений: в GNOME и KDE Plasma она включена по умолчанию.",
          "GNOME: «Параметры» → «Уведомления», выключите «Не беспокоить». KDE Plasma: значок колокольчика в системном трее, выключите «Не беспокоить».",
        ],
      },
      {
        title: "В браузере",
        steps: [
          "Нажмите на значок слева от адреса сайта и разрешите «Уведомления». В Firefox: «Настройки» → «Приватность и защита» → «Разрешения» → «Уведомления» → «Параметры».",
          "Обновите страницу и снова нажмите «Подключить это устройство».",
        ],
      },
    ],
  },
  android: {
    label: "Android",
    sections: [
      {
        title: "На телефоне",
        steps: [
          "Откройте «Настройки» → «Приложения» → ваш браузер (Chrome, Яндекс Браузер, Samsung Internet) → «Уведомления» и включите их.",
          "Выключите режим «Не беспокоить» в шторке быстрых настроек.",
          "Чтобы уведомления приходили при закрытом браузере, откройте настройки батареи для браузера и выберите «Без ограничений»: экономия заряда на многих телефонах отключает фоновые сообщения.",
        ],
      },
      {
        title: "В браузере",
        steps: [
          "В Chrome: меню с тремя точками → «Настройки» → «Настройки сайтов» → «Уведомления». Сайт ERP не должен стоять в списке заблокированных.",
          "Либо нажмите на значок слева от адреса сайта → «Разрешения» → «Уведомления» → «Разрешить».",
        ],
      },
    ],
  },
  ios: {
    label: "iPhone и iPad",
    sections: [
      {
        title: "Установите ERP на экран «Домой»",
        steps: [
          "Уведомления на iPhone и iPad работают только в установленном веб-приложении и только на iOS и iPadOS 16.4 и новее.",
          "Откройте ERP в Safari, нажмите «Поделиться» и выберите «На экран «Домой»».",
          "Запустите ERP с иконки на экране «Домой», а не из Safari, и нажмите «Подключить это устройство». Разрешите уведомления в появившемся окне.",
        ],
      },
      {
        title: "В настройках iPhone",
        steps: [
          "Откройте «Настройки» → «Уведомления» и найдите в списке ERP (она появляется там после первого запроса разрешения). Включите «Допуск уведомлений».",
          "Если ERP в списке нет: откройте её с иконки на экране «Домой», в разделе «Уведомления» нажмите «Подключить это устройство» и выберите «Разрешить».",
          "Если вы отклонили запрос и переключатель не помогает: удалите иконку ERP с экрана «Домой», добавьте её заново и подключите устройство ещё раз.",
          "Отключите «Не беспокоить» или «Фокусирование» в Пункте управления.",
        ],
      },
    ],
  },
};

const order: DevicePlatform[] = ["windows", "macos", "android", "ios", "linux"];

/**
 * Подсказка «Не видно уведомлений?» с шагами для устройства человека.
 *
 * Раньше здесь был один абзац про macOS, хотя большинство сотрудников сидит на
 * Windows и телефонах. Устройство определяется автоматически, но его можно
 * сменить: настраивать иногда приходится чужой телефон или второй компьютер.
 */
export function NotificationDeviceHelp({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const detected = currentPlatform();
  const [platform, setPlatform] = useState<DevicePlatform>(detected ?? "windows");
  const panelId = useId();
  const guide = guides[platform];
  // Уже в установленном приложении шаг «Установите ERP» не нужен: остаются настройки iPhone.
  const inInstalledApp = platform === "ios" && isStandalone();
  const sections = inInstalledApp ? guide.sections.slice(1) : guide.sections;

  return (
    <div className="notification-help">
      <button
        className="notification-help__toggle"
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => onOpenChange(!open)}
      >
        <span>Не видно уведомлений? Что проверить</span>
        <HiChevronDown aria-hidden="true" />
      </button>

      <div id={panelId} className="notification-help__panel" hidden={!open}>
        <div className="notification-help__platforms" role="group" aria-label="Устройство">
          {order.map((key) => (
            <button
              key={key}
              className="notification-help__platform"
              type="button"
              aria-pressed={platform === key}
              onClick={() => setPlatform(key)}
            >
              {guides[key].label}
            </button>
          ))}
        </div>
        <p className="notification-help__detected dw-secondary">
          {detected === null
            ? "Не удалось определить ваше устройство — выберите его выше."
            : detected === platform
              ? inInstalledApp
                ? "Это ваше устройство, ERP уже установлена на экран «Домой»."
                : "Это ваше устройство."
              : `Вы открыли ERP с другого устройства: ${guides[detected].label}. Показаны шаги для «${guide.label}».`}
        </p>

        <div className="notification-help__sections">
          {sections.map((section) => (
            <section key={section.title}>
              <h3>{section.title}</h3>
              <ol>
                {section.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </section>
          ))}
        </div>

        <p className="notification-help__footer dw-secondary">
          Когда всё настроено, нажмите «Проверить уведомления». Сначала появится проверка показа:
          она подтверждает, что браузер и система пропускают уведомления. Вторым придёт
          уведомление с сервера — но только если включена доставка «На устройство»; после
          проверки мы подскажем, как её включить.
        </p>
      </div>
    </div>
  );
}
